import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { categories, articles } from '@/mocks/site';
import { Link, useParams } from 'react-router-dom';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const categorySeo = {
    title: category
      ? `${category.name} - 黑料吃瓜网 | 最新${category.name}爆料合集`
      : '分类 - 黑料吃瓜网',
    description: category
      ? `${category.description}。黑料吃瓜网为您提供最新、最全的${category.name}相关爆料内容。`
      : '黑料吃瓜网分类浏览页面',
    keywords: `${category?.name || ''},黑料吃瓜,爆料,吃瓜`,
    canonical: `https://www.heiliaochigua.com/category/${slug}`,
  };

  useSEO(categorySeo);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-900">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - 黑料吃瓜网`,
          description: categorySeo.description,
          url: categorySeo.canonical,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.heiliaochigua.com' },
              { '@type': 'ListItem', position: 2, name: category.name, item: categorySeo.canonical },
            ],
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'https://www.heiliaochigua.com' },
            { name: category.name, url: categorySeo.canonical },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
              <i className={`${category.icon} text-lg text-primary-600`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
            </div>
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - 黑料吃瓜网`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
                        {article.tags[0]}
                      </span>
                    </div>
                    {article.isHot && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 text-xs font-bold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                          HOT
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h2 className="font-bold font-heading text-foreground-900 text-sm md:text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-foreground-500 text-xs mt-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                      <div className="flex items-center gap-3 text-foreground-400 text-xs">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400">{article.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}